import { useState, useEffect, useContext } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'
import { 
  Menu, X, BarChart3, PieChart, Settings, LogOut, ChevronRight, 
  TrendingUp, Lightbulb, ListTodo, Calculator, Bell, ChevronDown, ChevronUp, Target, Users, Shield, CalendarDays, Coins, Wallet,
  MessageSquare, Activity
} from 'lucide-react'
import axios from 'axios'

const API = process.env.REACT_APP_BACKEND_URL

export const HamburgerMenu = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    finanzas: true,
    progreso: true
  })
  const [sharedCount, setSharedCount] = useState(0)
  const { user, logout, token } = useAuth()
  const { t, locale } = useLanguage()
  const navigate = useNavigate()
  
  const lang = locale.startsWith('es') ? 'es' : 'en'

  // Fetch shared spaces count
  useEffect(() => {
    const fetchSharedCount = async () => {
      if (!token) return
      try {
        const res = await axios.get(`${API}/api/collaborations/shared-with-me`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const total = (res.data.shared_spaces?.length || 0) + (res.data.pending_invitations || 0)
        setSharedCount(total)
      } catch (error) {
        console.error('Error fetching shared count:', error)
      }
    }
    fetchSharedCount()
  }, [token])

  // Reorganized menu sections (NUEVA ESTRUCTURA)
  const menuSections = [
    {
      id: 'daily',
      title: lang === 'es' ? 'Diario' : 'Daily',
      items: [
        { path: '/kaizen', icon: Target, label: lang === 'es' ? 'Plan Kaizen' : 'Kaizen Plan' },
        { path: '/calendario', icon: CalendarDays, label: lang === 'es' ? 'Calendario' : 'Calendar' },
        { path: '/recordatorios', icon: Bell, label: lang === 'es' ? 'Recordatorios' : 'Reminders' },
        { path: '/ideas', icon: Lightbulb, label: lang === 'es' ? 'Ideas (Inbox)' : 'Ideas (Inbox)' },
      ]
    },
    {
      id: 'finanzas',
      title: lang === 'es' ? 'Finanzas' : 'Finance',
      collapsible: true,
      items: [
        { path: '/finanzas', icon: Wallet, label: lang === 'es' ? 'Finanzas' : 'Finance' },
        { path: '/analisis-financiero', icon: TrendingUp, label: lang === 'es' ? 'Análisis Financiero' : 'Financial Analysis' },
        { path: '/inversiones', icon: Coins, label: lang === 'es' ? 'Inversiones' : 'Investments' },
        { path: '/compound', icon: Calculator, label: lang === 'es' ? 'Interés Compuesto' : 'Compound Interest' },
      ]
    },
    {
      id: 'progreso',
      title: lang === 'es' ? 'Progreso' : 'Progress',
      collapsible: true,
      items: [
        { path: '/progreso', icon: BarChart3, label: lang === 'es' ? 'Evolución' : 'Evolution' },
        { path: '/ayuno-stats', icon: PieChart, label: lang === 'es' ? 'Progreso Ayuno' : 'Fasting Progress' },
        { path: '/tareas/analytics', icon: ListTodo, label: lang === 'es' ? 'Análisis de Tareas' : 'Task Analysis' },
      ]
    },
    {
      id: 'security',
      title: lang === 'es' ? 'Seguridad' : 'Security',
      items: [
        { path: '/vault', icon: Shield, label: lang === 'es' ? 'Bóveda de Documentos' : 'Document Vault' },
      ]
    },
    {
      id: 'collaboration',
      title: lang === 'es' ? 'Colaboración' : 'Collaboration',
      items: [
        { 
          path: '/shared-with-me', 
          icon: Users, 
          label: lang === 'es' ? 'Espacios compartidos' : 'Shared with me',
          badge: sharedCount > 0 ? sharedCount : null
        },
      ]
    },
    {
      id: 'feedback',
      title: lang === 'es' ? 'Feedback' : 'Feedback',
      items: [
        { path: '/feedback', icon: MessageSquare, label: lang === 'es' ? 'Enviar feedback' : 'Send feedback' },
        // Admin options - only visible to carlos.schz.fdez@gmail.com
        ...(user?.email === 'carlos.schz.fdez@gmail.com' ? [
          { path: '/feedback-admin', icon: BarChart3, label: lang === 'es' ? 'Feedback recibido' : 'Received feedback' },
          { path: '/testers', icon: Users, label: lang === 'es' ? 'Testers registrados' : 'Registered testers' },
          { path: '/pilot-metrics', icon: Activity, label: lang === 'es' ? 'Métricas del piloto' : 'Pilot metrics' }
        ] : [])
      ]
    },
    {
      id: 'system',
      title: lang === 'es' ? 'Sistema' : 'System',
      items: [
        { path: '/settings', icon: Settings, label: lang === 'es' ? 'Configuración' : 'Settings' },
      ]
    }
  ]

  const handleLogout = () => {
    logout()
    setIsOpen(false)
    navigate('/auth')
  }

  const handleNavClick = () => {
    setIsOpen(false)
  }

  const toggleSection = (sectionId) => (e) => {
    e.preventDefault()
    e.stopPropagation()
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  return (
    <>
      {/* Header Bar - with safe area padding for Dynamic Island */}
      <header className="hamburger-menu" data-testid="hamburger-menu">
        <div className="flex items-center justify-between px-4 py-3 max-w-5xl mx-auto">
          {/* Logo - Using the actual KaizenOS K logo */}
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_lifelog-25/artifacts/99y87i4v_Captura%20de%20pantalla%202026-02-12%20a%20la%28s%29%201.21.44%E2%80%AFp.m..png" 
              alt="KaizenOS Logo" 
              className="w-8 h-8 rounded-lg object-cover"
            />
            <span className="text-lg font-bold">
              Kaizen<span className="text-sky-400">OS</span>
            </span>
          </div>
          
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 rounded-full hover:bg-secondary transition-colors"
            data-testid="hamburger-toggle"
            aria-label={isOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          >
            {isOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Menu Content */}
            <div className="hamburger-menu-content z-50 max-h-[80vh] overflow-y-auto" data-testid="hamburger-dropdown">
              {/* User Info */}
              <div className="px-4 py-4 border-b bg-secondary/30">
                <p className="font-semibold">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>

              {/* Menu Sections */}
              {menuSections.map((section, sectionIndex) => {
                const isExpanded = section.collapsible ? expandedSections[section.id] : true
                
                return (
                  <div key={section.id}>
                    {/* Section Header */}
                    {section.collapsible ? (
                      <button
                        onClick={toggleSection(section.id)}
                        className="w-full flex items-center justify-between px-4 pt-5 pb-2 text-[13px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider bg-gray-50/80 dark:bg-gray-800/40 border-b border-gray-200/60 dark:border-gray-700/40 hover:bg-gray-100/80 dark:hover:bg-gray-800/60 transition-colors"
                      >
                        <span>{section.title}</span>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    ) : (
                      <div className="px-4 pt-5 pb-2 text-[13px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider bg-gray-50/80 dark:bg-gray-800/40 border-b border-gray-200/60 dark:border-gray-700/40">
                        {section.title}
                      </div>
                    )}
                    
                    {/* Section Items */}
                    {isExpanded && (
                      <nav className="py-1">
                        {section.items.map((item) => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={handleNavClick}
                            className={({ isActive }) => cn(
                              'flex items-center gap-3 px-4 py-3 transition-colors',
                              isActive 
                                ? 'bg-amber-500/10 text-amber-500 font-medium' 
                                : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
                            )}
                            data-testid={`menu-${item.path.replace(/\//g, '-').slice(1)}`}
                          >
                            {({ isActive }) => (
                              <>
                                <item.icon className={cn(
                                  "w-5 h-5",
                                  isActive ? 'text-amber-500' : 'text-muted-foreground'
                                )} />
                                <span className="flex-1">{item.label}</span>
                                {item.badge && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded-full">
                                    {item.badge}
                                  </span>
                                )}
                                <ChevronRight className={cn(
                                  "w-4 h-4",
                                  isActive ? 'text-amber-500/50' : 'opacity-50'
                                )} />
                              </>
                            )}
                          </NavLink>
                        ))}
                      </nav>
                    )}
                  </div>
                )
              })}

              {/* Logout */}
              <div className="border-t py-2 mt-2">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 w-full text-left text-destructive hover:bg-destructive/10 transition-colors"
                  data-testid="menu-logout"
                >
                  <LogOut className="w-5 h-5" />
                  <span>{t('auth.logout')}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </header>

      {/* Spacer to push content below fixed header - accounts for Dynamic Island */}
      <div style={{ height: 'calc(56px + env(safe-area-inset-top, 0px))' }} />
    </>
  )
}
